import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

/**
 * UC-A-2 footer contract: panels keep their save-per-row API; each open editor registers one pending row
 * ({ id, label, save, discard }). The footer counts them, Save runs them sequentially (shiftSheetRules.savePendingRows)
 * and hands the first business-rule error back to the owning row for inline display.
 */
const Ctx = createContext(null);

export function ShiftSheetDraftProvider({ children }) {
  const rowsRef = useRef(new Map());
  const [version, setVersion] = useState(0);
  const [inlineErrors, setInlineErrors] = useState({});
  const bump = () => setVersion((v) => v + 1);

  const register = useCallback((row) => { rowsRef.current.set(row.id, row); bump(); }, []);
  const unregister = useCallback((id) => { if (rowsRef.current.delete(id)) bump(); setInlineErrors((m) => { if (!(id in m)) return m; const n = { ...m }; delete n[id]; return n; }); }, []);
  const setInlineError = useCallback((id, message) => setInlineErrors((m) => ({ ...m, [id]: message })), []);
  const pending = useMemo(() => Array.from(rowsRef.current.values()), [version]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(() => ({ pending, register, unregister, inlineErrors, setInlineError }), [pending, register, unregister, inlineErrors, setInlineError]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

const NOOP = { pending: [], register: () => {}, unregister: () => {}, inlineErrors: {}, setInlineError: () => {} };
export const useShiftSheetDraft = () => useContext(Ctx) || NOOP;
